<template>
    <div class="image-answer-picker mt-4">
        <!-- preview while preparing / uploading / failed -->
        <div v-if="previewUrl" class="picker-box picker-box--preview">
            <div class="preview-wrapper">
                <img :src="previewUrl" alt="" class="preview-img" :class="{'preview-img--dim': state !== 'failed'}" />
                <div v-if="state !== 'failed'" class="preview-overlay">
                    <v-progress-circular :indeterminate="state === 'preparing' || progress === null" :value="progress" size="56" width="5" color="white">
                        <span v-if="state === 'uploading' && progress !== null" class="white--text font-weight-bold">{{progress}}%</span>
                    </v-progress-circular>
                    <div class="white--text mt-3 font-weight-medium">{{state === 'preparing' ? 'جاري تجهيز الصورة...' : 'جاري رفع الصورة...'}}</div>
                </div>
            </div>
            <template v-if="state === 'failed'">
                <div class="error--text mt-3 d-flex align-center justify-center text-center">
                    <v-icon color="error" small class="me-1">mdi-alert-circle-outline</v-icon>
                    {{errorText}}
                </div>
                <div class="actions mt-3">
                    <v-btn color="primary" depressed rounded :disabled="disabled" @click="retry">
                        <v-icon left>mdi-refresh</v-icon>
                        إعادة المحاولة
                    </v-btn>
                    <v-btn text rounded @click="reset">إلغاء</v-btn>
                </div>
            </template>
        </div>

        <!-- idle -->
        <div v-else class="picker-box">
            <v-icon size="36" color="primary" class="mb-2">mdi-image-plus-outline</v-icon>
            <div class="strong-text--text font-weight-bold">أرفق صورة لإجابتك <span class="muted--text font-weight-regular">(اختياري)</span></div>
            <div class="muted--text text-caption mt-1">صوّر ورقة الإجابة كاملة في إضاءة جيدة</div>
            <div v-if="busy" class="primary--text text-caption mt-2">{{state === 'preparing' ? 'جاري تجهيز الصورة...' : 'جاري رفع الصورة...' + (progress !== null ? ' ' + progress + '%' : '')}}</div>
            <div v-else-if="errorText" class="error--text text-caption mt-2">{{errorText}}</div>
            <div class="actions mt-4">
                <v-btn v-if="canCapture" color="primary" depressed rounded :disabled="disabled || busy" @click="open('cameraInput')">
                    <v-icon left>mdi-camera</v-icon>
                    التقاط صورة
                </v-btn>
                <v-btn color="primary" :outlined="canCapture" :depressed="!canCapture" rounded :disabled="disabled || busy" @click="open('galleryInput')">
                    <v-icon left>mdi-image-multiple-outline</v-icon>
                    {{canCapture ? 'اختيار من المعرض' : 'رفع صورة'}}
                </v-btn>
            </div>
        </div>

        <input ref="cameraInput" type="file" accept="image/*" capture="environment" class="d-none" @change="onFileChange" />
        <input ref="galleryInput" type="file" accept="image/*" class="d-none" @change="onFileChange" />
    </div>
</template>

<script>
const MAX_DIMENSION = 2000;          // px, longest side — keeps handwriting sharp
const JPEG_QUALITY = 0.85;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const MAX_INPUT_BYTES = 40 * 1024 * 1024;

function loadImage(file){
    return new Promise((resolve, reject)=>{
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = ()=>resolve({img, url});
        img.onerror = ()=>{ URL.revokeObjectURL(url); reject(new Error('decode')); };
        img.src = url;
    });
}

function canvasToBlob(canvas){
    return new Promise((resolve, reject)=>{
        canvas.toBlob(blob=>blob ? resolve(blob) : reject(new Error('encode')), 'image/jpeg', JPEG_QUALITY);
    });
}

// Downscale + re-encode to JPEG. This also strips EXIF metadata (e.g. GPS location).
async function prepareImage(file){
    let loaded;
    try {
        loaded = await loadImage(file);
    } catch (e) {
        // browser can't decode this format (e.g. HEIC on Android); send as-is and let the server validate
        if (file.size > MAX_UPLOAD_BYTES) throw new Error('too-large');
        return {blob: file, previewUrl: null};
    }
    const {img, url} = loaded;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // transparent PNGs would otherwise turn black
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await canvasToBlob(canvas);
    canvas.width = canvas.height = 0; // free memory early (iOS)
    if (blob.size > MAX_UPLOAD_BYTES) {
        URL.revokeObjectURL(url);
        throw new Error('too-large');
    }
    return {blob, previewUrl: url};
}

export default {
    name: 'ExamImageAnswerPicker',
    props: {
        // (blob, onProgress) => Promise
        upload: {type: Function, required: true},
        disabled: Boolean,
    },
    data(){
        return {
            state: 'idle', // idle | preparing | uploading | failed
            previewUrl: null,
            prepared: null,
            progress: null,
            errorText: null,
        };
    },
    computed: {
        busy(){
            return this.state === 'preparing' || this.state === 'uploading';
        },
        canCapture(){
            if (process.env.VUE_APP_PLATFORM === 'android' || process.env.VUE_APP_PLATFORM === 'ios') return true;
            // same rule as the app's `phoneOnly` — any phone-sized screen gets the camera button
            if (!this.$vuetify.breakpoint.mdAndUp) return true;
            // mobile browsers honour the `capture` attribute; desktops ignore it and just open a file picker
            const ua = navigator.userAgent || '';
            if (/Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua)) return true;
            if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true; // iPadOS reports as Mac
            return !!(window.matchMedia && window.matchMedia('(pointer: coarse), (any-pointer: coarse)').matches);
        },
    },
    beforeDestroy(){
        this.revokePreview();
    },
    methods: {
        open(ref){
            if (this.disabled || this.busy) return;
            this.errorText = null;
            this.$refs[ref].click();
        },
        async onFileChange(event){
            const input = event.target;
            const file = input.files && input.files[0];
            input.value = ''; // allow picking the same file again
            if (!file) return;

            if (file.type && !file.type.startsWith('image/')) {
                this.errorText = 'الملف المختار ليس صورة، اختر صورة من فضلك';
                return;
            }
            if (file.size > MAX_INPUT_BYTES) {
                this.errorText = 'حجم الصورة كبير جدًا، اختر صورة أصغر';
                return;
            }

            this.revokePreview();
            this.errorText = null;
            this.state = 'preparing';
            try {
                const prepared = await prepareImage(file);
                if (this._isDestroyed) { if (prepared.previewUrl) URL.revokeObjectURL(prepared.previewUrl); return; }
                this.prepared = prepared.blob;
                this.previewUrl = prepared.previewUrl;
            } catch (e) {
                this.state = 'idle';
                this.errorText = e.message === 'too-large'
                    ? 'حجم الصورة كبير جدًا، اختر صورة أصغر'
                    : 'تعذرت قراءة الصورة، جرّب صورة أخرى';
                return;
            }
            this.send();
        },
        send(){
            this.state = 'uploading';
            this.progress = null;
            this.errorText = null;
            this.upload(this.prepared, progress=>{ this.progress = progress; }).then(()=>{
                if (this._isDestroyed) return;
                this.reset();
            }).catch(error=>{
                if (this._isDestroyed) return;
                const status = error && error.response && error.response.status;
                this.errorText = status === 413
                    ? 'حجم الصورة أكبر من المسموح'
                    : (status ? 'تعذر حفظ الصورة، حاول مرة أخرى' : 'تعذر رفع الصورة، تحقق من اتصالك بالإنترنت');
                if (this.previewUrl) {
                    this.state = 'failed';
                } else {
                    // no preview (format the browser can't render) — fall back to the idle view showing the error
                    this.state = 'idle';
                    this.prepared = null;
                }
            });
        },
        retry(){
            if (this.prepared && !this.disabled) this.send();
        },
        reset(){
            this.revokePreview();
            this.prepared = null;
            this.progress = null;
            this.state = 'idle';
            this.errorText = null;
        },
        revokePreview(){
            if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = null;
        },
    },
};
</script>

<style lang="scss" scoped>
.picker-box{
    border: 2px dashed rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 20px 16px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}
.picker-box--preview{
    border-style: solid;
}
.actions{
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin: -4px;
    .v-btn{
        margin: 4px;
    }
}
.preview-wrapper{
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    max-width: 100%;
    line-height: 0;
}
.preview-img{
    max-width: 100%;
    max-height: 320px;
    object-fit: contain;
    transition: filter .2s;
}
.preview-img--dim{
    filter: brightness(.45);
}
.preview-overlay{
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: normal;
}
</style>
